import { Button } from '@crowdex/ui/components/button';
import type { WorkspacePort } from '@crowdex/workspace-protocol';
import { ExternalLink, Network, Square } from 'lucide-react';
import { PanelEmpty } from '@/components/desktop-panel-primitives';
import { openForwardedPort } from '@/lib/cloud-runtime/port-forwards';
import type { SandboxPorts } from '@/lib/side-panel/use-sandbox-ports';

/** The screen's one filled button opens the first port; the others' are outline (D38). */
function portActionVariant(index: number): 'default' | 'outline' {
  return index === 0 ? 'default' : 'outline';
}

/** "node · pnpm --filter web serve": what listens, and the command under it. */
export function splitProcess(process: string): { name: string; command: string | null } {
  const at = process.indexOf(' · ');
  if (at < 0) return { name: process, command: null };
  return { name: process.slice(0, at), command: process.slice(at + 3) };
}

function PortHeading({ port, process }: { port: number; process: string }) {
  const { name, command } = splitProcess(process);
  return (
    <div className="flex items-center gap-3">
      <span className="font-mono font-semibold text-base tabular-nums">{port}</span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="type-ui truncate font-medium">{name}</span>
        {command !== null && (
          <span className="type-caption truncate font-mono text-muted-foreground">{command}</span>
        )}
      </span>
    </div>
  );
}

/**
 * The servers of a conversation on this computer (D38): what listens on each port, opened at
 * `localhost` in the panel's browser.
 */
export function LocalPortsView({
  ports,
  onOpen,
}: {
  ports: readonly WorkspacePort[];
  onOpen: (port: number) => void;
}) {
  if (ports.length === 0)
    return (
      <PanelEmpty
        icon={Network}
        title="No open ports"
        text="When something in this conversation's workspace listens on a port (a dev server), it appears here."
      />
    );
  return (
    <div className="flex h-full min-h-0 flex-col gap-2 overflow-y-auto px-4 pt-1 pb-4">
      <p className="type-meta text-muted-foreground">Servers running on this computer.</p>
      {[...ports]
        .sort((left, right) => left.port - right.port)
        .map((entry, index) => (
          <div className="flex flex-col gap-3 rounded-2xl border bg-card p-3" key={entry.port}>
            <PortHeading port={entry.port} process={entry.process ?? 'Unknown process'} />
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => onOpen(entry.port)}
                size="sm"
                variant={portActionVariant(index)}
              >
                <ExternalLink />
                Open in Browser
              </Button>
            </div>
          </div>
        ))}
    </div>
  );
}

/**
 * The ports the cloud sandbox listens on, and their forwards to this machine: a forwarded port
 * opens in the user's own browser at `localhost`.
 */
export function PortsView({ ports }: { ports: SandboxPorts }) {
  if (ports.ports.length === 0 && ports.forwards.size === 0)
    return (
      <PanelEmpty
        icon={Network}
        title="No open ports"
        text="When something in the cloud sandbox listens on a port (a dev server), it appears here to be forwarded to your machine."
      />
    );
  const listed = [
    ...ports.ports,
    // A forward whose port closed stays listed until it is stopped.
    ...[...ports.forwards.keys()]
      .filter((port) => !ports.ports.some((open) => open.port === port))
      .map((port) => ({ port, process: null, closed: true as const })),
  ].sort((left, right) => left.port - right.port);
  // D38: one card per port, what listens on it, and how to open it.
  return (
    <div className="flex h-full min-h-0 flex-col gap-2 overflow-y-auto px-4 pt-1 pb-4">
      <p className="type-meta text-muted-foreground">
        Servers running in this conversation's sandbox.
      </p>
      {!ports.canForward && (
        <p className="type-caption text-muted-foreground">
          Forwarding a port to your machine needs Crowdex Desktop or <code>crowdex serve</code>{' '}
          running on it.
        </p>
      )}
      {listed.map((entry, index) => {
        const forward = ports.forwards.get(entry.port);
        const closed = 'closed' in entry;
        return (
          <div className="flex flex-col gap-3 rounded-2xl border bg-card p-3" key={entry.port}>
            <div className="flex items-center gap-3">
              <span className="font-mono font-semibold text-base tabular-nums">{entry.port}</span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="type-ui truncate font-medium">
                  {closed ? 'Not listening anymore' : (entry.process ?? 'Unknown process')}
                </span>
                {forward !== undefined && (
                  <span className="type-caption truncate font-mono text-muted-foreground">
                    localhost:{forward.localPort}
                  </span>
                )}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {forward !== undefined ? (
                <>
                  <Button
                    disabled={closed}
                    onClick={() => openForwardedPort(forward.localPort)}
                    size="sm"
                    variant={portActionVariant(index)}
                  >
                    <ExternalLink />
                    Open in Browser
                  </Button>
                  <Button
                    aria-label={`Stop forwarding port ${entry.port}`}
                    onClick={() => void ports.stop(entry.port)}
                    size="sm"
                    variant="outline"
                  >
                    <Square />
                    Stop
                  </Button>
                </>
              ) : (
                <Button
                  disabled={!ports.canForward}
                  onClick={() => void ports.forward(entry.port, true)}
                  size="sm"
                  variant={portActionVariant(index)}
                >
                  Forward and open
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
